import { Package } from 'lucide-react';
import { useMemo } from 'react';
import type { Quantity } from '~/graphql/graphql';
import { useTranslation } from 'react-i18next';
import type { GetCartQuery } from '~/graphql/graphql';
type OrderItem = NonNullable<
  NonNullable<GetCartQuery['cart']>['items']
>[number];
import {
  formatCurrency,
  formatLengthWithBasis,
  getLinePiecePrices,
  isRandomTallyItem,
  priceUnitLabel,
  roundTo6dp,
  roundToCents,
} from '~/utils';
import { PricePerPieceTooltip } from '~/components/ui';
import { TarpStatus } from '~/components/ui/TarpStatus';
import { VehiclePayloadLabel } from '~/components/ui/VehiclePayloadLabel';
import {
  getPlatformProductDimensions,
  getGradeInformation,
  getListedPricePerUnit,
  getVehiclePayload,
  groupItemsByTally,
} from '~/utils/order';
import { unitsPerPiece } from '~/utils/unitConversion';
import type { TransportMode } from '~/hooks/useTransportState';

interface OrderItemProps {
  name: string;
  gradeInfo: string;
  lengthDisplayValue: string;
  quantity: number;
  unit: string;
  price: number | null;
  /** Currency code for all price displays — defaults to 'USD'. */
  currency?: string;
  total: number | null;
  suggestedPrice?: number | null;
  suggestedTotal?: number | null;
  vehiclePayload?: Quantity | null;
  vehicleLabel?: TransportMode;
  piecesPerBundle?: Quantity | null;
  /** Pieces that fit in one vehicle (shown next to capacity when unit ≠ PCS). */
  vehiclePiecesCount?: number;
  /** Total pieces in this line item (shown next to pieces-per-bundle). */
  totalPieces?: number;
  /** 6dp-rounded price per piece — used for total and shown when unit ≠ PCS. */
  pricePerPiece?: number;
  /** Price per piece from buyer's suggested price — shown when unit ≠ PCS and a suggestion exists. */
  suggestedPricePerPiece?: number;
}

export function OrderItem({
  name,
  gradeInfo,
  lengthDisplayValue,
  quantity,
  unit,
  price,
  currency = 'USD',
  total,
  suggestedPrice,
  suggestedTotal,
  vehiclePayload,
  vehicleLabel,
  piecesPerBundle,
  vehiclePiecesCount,
  totalPieces,
  pricePerPiece,
  suggestedPricePerPiece,
}: Readonly<OrderItemProps>) {
  const { t } = useTranslation(['order', 'cart']);
  return (
    <div className="flex gap-4 pb-4 border-b border-neut-100 last:border-0 last:pb-0">
      <div className="w-16 h-16 bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg flex items-center justify-center shrink-0">
        <div className="text-xl">🌲</div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm text-neut-prim font-medium mb-1 truncate">
          {name}
        </h4>
        <p className="text-xs text-neut-sec mb-1">
          {t('cart:cart.cart_grade')}: {gradeInfo}
          <span className="ml-2 px-2 py-1 bg-success-100 text-success-600 rounded-md">
            {lengthDisplayValue}
          </span>
        </p>
        <p className="flex items-center gap-1 text-xs text-neut-500">
          {quantity} {unit} ×{' '}
          {price != null
            ? `${formatCurrency(price, currency)}/${priceUnitLabel(unit)}`
            : t('cart:cart.price_not_available')}
          {pricePerPiece != null && unit === 'PCS' && (
            <PricePerPieceTooltip
              pricePerPiece={pricePerPiece}
              ns="cart"
              iconOnly
              className="text-neut-500"
            />
          )}
        </p>
        {pricePerPiece != null && unit !== 'PCS' && (
          <PricePerPieceTooltip
            pricePerPiece={pricePerPiece}
            ns="cart"
            className="text-xs font-semibold text-neut-500"
          />
        )}
        {vehiclePayload != null &&
          vehiclePayload.value > 0 &&
          vehicleLabel != null && (
            <VehiclePayloadLabel
              quantity={vehiclePayload}
              vehicleLabel={vehicleLabel}
              piecesCount={vehiclePiecesCount}
            />
          )}
        {piecesPerBundle != null && piecesPerBundle.value > 0 && (
          <p className="text-xs text-neut-sec mt-1 flex items-center gap-1">
            <Package size={12} aria-hidden="true" />
            {piecesPerBundle.value} {piecesPerBundle.unit}/
            {t('cart:cart.bundles_one')}
            {totalPieces != null && unit !== 'PCS' && (
              <strong className="text-neut-sec">
                ({Math.round(totalPieces)} PCS)
              </strong>
            )}
          </p>
        )}
        {suggestedPrice != null && suggestedPrice > 0 && (
          <div className="mt-2 p-2 bg-warning-50 border border-warning-200 rounded-lg">
            <p className="text-xs text-warning-700">
              {t('order.your_offer')} {formatCurrency(suggestedPrice, currency)}
              /{priceUnitLabel(unit)}
              {suggestedPricePerPiece != null && unit !== 'PCS' && (
                <span className="font-semibold ml-1">
                  ({formatCurrency(suggestedPricePerPiece, currency)}/
                  {t('cart:cart.pc_unit')})
                </span>
              )}
            </p>
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <p
          className="text-sm text-neut-prim font-semibold"
          data-testid="item-line-total"
        >
          {total != null
            ? formatCurrency(total, currency)
            : t('cart:cart.price_not_available')}
        </p>
        {suggestedPrice != null &&
          suggestedPrice > 0 &&
          suggestedTotal != null && (
            <p
              className="text-sm text-warning-700"
              data-testid="item-suggested-line-total"
            >
              {formatCurrency(suggestedTotal, currency)}
            </p>
          )}
      </div>
    </div>
  );
}

export function OrderItems({
  items,
  title,
  selectedTransportVehicleId,
  vehicleLabel,
  tarp,
  currency,
  incoTerm,
}: Readonly<{
  items: OrderItem[];
  title: string;
  selectedTransportVehicleId?: string;
  vehicleLabel?: TransportMode;
  tarp?: boolean;
  currency?: string | null;
  incoTerm?: string | null;
}>) {
  const { t } = useTranslation('order-builder');

  const { tallyGroups, regularItems } = useMemo(
    () => groupItemsByTally(items),
    [items]
  );

  const renderItem = (item: OrderItem) => {
    const ppl = item.platformProductsLengths;
    const product = ppl.platformProduct;
    const vehiclePayload = selectedTransportVehicleId
      ? getVehiclePayload(
          ppl.platformProductVehicleCapacities,
          selectedTransportVehicleId
        )
      : undefined;
    const dims = getPlatformProductDimensions(
      product,
      ppl.length,
      ppl.isNominal
    );
    const hasDims =
      dims.lengthInches > 0 && dims.widthInches > 0 && dims.thicknessInches > 0;
    let vehiclePiecesCount: number | undefined;
    const isRandomTally = isRandomTallyItem(item);
    const totalPieces: number | undefined = isRandomTally
      ? undefined
      : item.quantityStored.value;
    const { listedPricePerPiece, suggestedPerPiece } = getLinePiecePrices(
      item,
      incoTerm
    );
    const pricePerPiece: number | undefined =
      listedPricePerPiece != null
        ? roundTo6dp(listedPricePerPiece.amount)
        : undefined;
    // RAW (unrounded) per-piece amount — used ONLY for total arithmetic so the
    // multiply-first-round-once contract matches calculateOrderTotals. The
    // 6dp-rounded `pricePerPiece` above stays reserved for display text.
    const pricePerPieceRaw: number | undefined = listedPricePerPiece?.amount;
    if (hasDims) {
      try {
        if (vehiclePayload && vehiclePayload.value > 0) {
          const uppVehicle = unitsPerPiece(
            vehiclePayload.unit,
            dims.lengthInches,
            dims.widthInches,
            dims.thicknessInches
          );
          if (uppVehicle > 0)
            vehiclePiecesCount = vehiclePayload.value / uppVehicle;
        }
      } catch {
        // unsupported unit combination — omit vehicle piece count
      }
    }
    // Seller's LISTED price — mirrors calculateOrderTotals' listedTotal standardSelector
    // (src/utils/index.tsx). Do NOT read item.effectivePrice here: the backend sets
    // effectivePrice to suggestedPrice whenever a suggestion exists (getEffectivePrice()),
    // so using it for the primary total would collapse the listed and suggested figures
    // into the same number (CR-01).
    const listedPrice = getListedPricePerUnit(item, incoTerm);
    const listedAmount = listedPrice.amount;
    // RAW (unrounded) suggested per-piece amount, mirroring calculateOrderTotals'
    // suggested pieceSelector (falls back to the RAW pricePerPiece, not the
    // 6dp-rounded display value) — must multiply raw and round once.
    const suggestedPricePerPieceRawForTotal: number | undefined =
      suggestedPerPiece != null && suggestedPerPiece.amount > 0
        ? suggestedPerPiece.amount
        : pricePerPieceRaw;
    const suggestedLineTotal: number | null =
      pricePerPieceRaw != null && totalPieces != null && totalPieces > 0
        ? // must mirror calculateOrderTotals' per-item formula
          roundToCents((suggestedPricePerPieceRawForTotal ?? 0) * totalPieces)
        : item.suggestedPrice != null
          ? roundToCents(item.suggestedPrice.amount * item.quantity.value)
          : null;
    return (
      <OrderItem
        key={ppl.id}
        name={product.name}
        gradeInfo={getGradeInformation(ppl)}
        lengthDisplayValue={formatLengthWithBasis(
          ppl.lengthDisplayValue,
          ppl.isNominal
        )}
        quantity={item.quantity.value}
        unit={item.quantity.unit}
        price={listedAmount}
        currency={currency ?? listedPrice.currency}
        total={
          pricePerPieceRaw != null && totalPieces != null && totalPieces > 0
            ? roundToCents(pricePerPieceRaw * totalPieces)
            : listedAmount != null
              ? roundToCents(listedAmount * item.quantity.value)
              : null
        }
        suggestedPrice={item.suggestedPrice?.amount}
        suggestedTotal={suggestedLineTotal}
        vehiclePayload={vehiclePayload}
        vehicleLabel={vehicleLabel}
        piecesPerBundle={{ value: item.effectivePiecesPerBundle, unit: 'PCS' }}
        vehiclePiecesCount={vehiclePiecesCount}
        totalPieces={totalPieces}
        pricePerPiece={pricePerPiece}
        suggestedPricePerPiece={
          suggestedPerPiece != null
            ? roundTo6dp(suggestedPerPiece.amount)
            : undefined
        }
      />
    );
  };

  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border p-6 shadow-neut-100">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-prim" />
          <h2 className="text-neut-prim font-semibold">{title}</h2>
        </div>
        <TarpStatus tarp={tarp} />
      </div>
      <div className="space-y-4">
        {Array.from(tallyGroups.entries()).map(
          ([groupKey, { isRandomTally, items: tallyItems }]) => (
            <div
              key={groupKey}
              className="border border-status-info-200 rounded-lg overflow-hidden"
            >
              <div className="bg-status-info-50 px-4 py-1.5 border-b border-status-info-200">
                <span className="text-xs font-semibold text-status-info-800">
                  {isRandomTally
                    ? t('order_builder.filters.random_tally')
                    : t('order_builder.filters.canned_tally')}
                </span>
              </div>
              <div className="p-4">{tallyItems.map(renderItem)}</div>
            </div>
          )
        )}
        {regularItems.map(renderItem)}
      </div>
    </div>
  );
}
